import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { tenantAPI } from '../../api/client';
import './NotificationBell.css';

const POLL_MS = 60000;

function ago(iso) {
  const t = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  const m = Math.round((Date.now() - t) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

/** A short two-note chime (no sound file needed). */
function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.12]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.3);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* no audio available */ }
}

/**
 * The bell: this person's notifications in this company (sent from Notification and Maintenance Control) with the
 * unread count. Opening the list marks what's shown as read. Checked every minute and when the tab comes back.
 * `sound` / `desktop` (Settings › Notifications): a chime and a browser notification when new ones arrive.
 */
export default function NotificationBell({ className = 'icon-button bell', sound = false, desktop = false, labels = {} }) {
  const [data, setData] = useState({ enabled: true, unread: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const seen = useRef(null); // ids already known, so only new arrivals chime
  const alerts = useRef({ sound, desktop });
  alerts.current = { sound, desktop };
  const load = useCallback(() => tenantAPI.notifications().then((r) => {
    const ids = r.data.notifications.map((n) => n.id);
    if (seen.current) {
      const fresh = r.data.notifications.filter((n) => !n.read && !seen.current.has(n.id));
      if (fresh.length) {
        if (alerts.current.sound) chime();
        if (alerts.current.desktop && 'Notification' in window && Notification.permission === 'granted') {
          fresh.slice(0, 3).forEach((n) => { try { new Notification(n.title, { body: n.body || '', tag: `tcx-${n.id}` }); } catch { /* blocked */ } });
        }
      }
    }
    seen.current = new Set(ids);
    setData(r.data);
  }).catch(() => {}), []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    const unseen = data.notifications.filter((n) => !n.read).map((n) => n.id);
    if (next && unseen.length) {
      tenantAPI.markNotificationsRead(unseen).then(() => setData((d) => ({ ...d, unread: Math.max(0, d.unread - unseen.length) }))).catch(() => {});
    }
  };
  const markAll = () => tenantAPI.markNotificationsRead(null).then(load).catch(() => {});
  if (!data.enabled) return null;

  return (
    <div className="tcx-bell" ref={ref}>
      <button type="button" className={className} title="Notifications" aria-expanded={open} onClick={toggle}
        aria-label={`Notifications${data.unread ? ` (${data.unread} unread)` : ''}`}>
        <Bell size={19} />{data.unread > 0 && <i className="tcx-bell-count">{data.unread > 99 ? '99+' : data.unread}</i>}
      </button>
      {open && (
        <div className="tcx-bell-panel" role="dialog" aria-label="Notifications">
          <div className="tcx-bell-head"><b>{labels.title || 'Notifications'}</b>
            {data.notifications.length > 0 && <button type="button" onClick={markAll}><CheckCheck size={13} /> {labels.markAll || 'Mark all read'}</button>}</div>
          <div className="tcx-bell-list">
            {data.notifications.map((n) => (
              <div key={n.id} className={`tcx-bell-item ${n.level} ${n.read ? '' : 'unread'}`}>
                <b>{n.title}</b>{n.body && <span>{n.body}</span>}
                <em>{ago(n.created_at)}{n.link && <> · <a href={n.link}>Open</a></>}</em>
              </div>
            ))}
            {!data.notifications.length && <div className="tcx-bell-empty">{labels.empty || "You're all caught up."}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
